import clsx from 'clsx'
import { Dropdown as BsDropdown } from 'react-bootstrap'
import { Fragment, MouseEvent, ReactNode } from 'react'
import { useMediaQuery } from 'react-responsive'
import { IconPaths, IconSize, KTSVG } from '../../../_metronic/helpers'
import { Button } from '../Button/Button'
import styles from './styles/DataTable.module.css'

type StackedCellProps = {
  primary?: ReactNode
  secondary?: ReactNode
  /** Dígitos tabulares (CNPJ, CPF, códigos). */
  tabular?: boolean
}

/**
 * Dado principal + linha de apoio, com truncamento e tooltip. Use para juntar duas colunas
 * relacionadas numa só (ex.: nome fantasia + razão social, CNPJ + CNES).
 */
export const StackedCell = (props: StackedCellProps) => {
  const { primary, secondary, tabular } = props
  const hasSecondaryLine = 'secondary' in props
  const vazio = (value: ReactNode) => value === undefined || value === null || value === ''

  return (
    <div className={clsx(tabular && styles.tabular)}>
      <span className={styles.primary} title={typeof primary === 'string' ? primary : undefined}>
        {vazio(primary) ? '—' : primary}
      </span>
      {hasSecondaryLine && (
        <span
          className={styles.secondary}
          title={typeof secondary === 'string' ? secondary : undefined}
        >
          {vazio(secondary) ? '—' : secondary}
        </span>
      )}
    </div>
  )
}

type ChipListCellProps = {
  items: string[]
  /** Quantos chips aparecem antes do "+N". */
  max?: number
  /** Texto quando não há itens. Sem ele, mostra o traço igual ao StackedCell vazio. */
  emptyLabel?: string
  /** Substantivo para o nome acessível do "+N" ("Mais 4 serviços: ..."). */
  noun?: { singular: string; plural: string }
}

/** Lista de chips neutros com "+N" (tooltip e aria-label trazem os itens ocultos). */
export const ChipListCell = ({
  items,
  max = 2,
  emptyLabel,
  noun = { singular: 'item', plural: 'itens' },
}: ChipListCellProps) => {
  if (!items.length) {
    return emptyLabel ? <span className={styles.muted}>{emptyLabel}</span> : <StackedCell />
  }

  const visible = items.slice(0, max)
  const rest = items.slice(max)

  return (
    <div className={styles.chips}>
      {visible.map((item) => (
        <span key={item} className={styles.chip} title={item}>
          {item}
        </span>
      ))}
      {rest.length > 0 && (
        <span
          className={styles.chipMore}
          tabIndex={0}
          title={rest.join(', ')}
          aria-label={`Mais ${rest.length} ${
            rest.length === 1 ? noun.singular : noun.plural
          }: ${rest.join(', ')}`}
        >
          +{rest.length}
        </span>
      )}
    </div>
  )
}

/** Contagem simples ("3 serviços"), útil como linha de apoio no mobile. */
export const countLabel = (count: number, noun: { singular: string; plural: string }) =>
  count === 0 ? `Nenhum ${noun.singular}` : `${count} ${count === 1 ? noun.singular : noun.plural}`

type StatusBadgeProps = {
  active?: boolean
  /** [rótulo ativo, rótulo inativo] — ajuste o gênero ao substantivo ("Ativa"/"Inativa"). */
  labels?: [string, string]
}

/** Badge suave no tamanho do texto; o rótulo carrega o significado junto com a cor. */
export const StatusBadge = ({ active, labels = ['Ativo', 'Inativo'] }: StatusBadgeProps) => (
  <span className={clsx(styles.status, active ? styles.statusSuccess : styles.statusDanger)}>
    {active ? labels[0] : labels[1]}
  </span>
)

export type StatusDotTone =
  | 'neutral'
  | 'warning'
  | 'info'
  | 'primary'
  | 'success'
  | 'danger'
  | 'dark'

type StatusDotProps = {
  label?: string
  tone?: StatusDotTone
}

/**
 * Situação com vários estados (fatura, nota…): rótulo + ponto colorido. O texto carrega o
 * significado — a cor é reforço —, então o contraste é o do texto comum. Sem rótulo: "—".
 * Para ativo/inativo use o StatusBadge.
 */
export const StatusDot = ({ label, tone = 'neutral' }: StatusDotProps) => {
  if (!label) return <span>—</span>
  return (
    <span className={styles.statusDot}>
      <span aria-hidden='true' className={clsx(styles.dot, styles[`dot_${tone}`])} />
      {label}
    </span>
  )
}

export type RowActionItem = {
  label: string
  onClick: () => void
  /** Ação destrutiva (vermelho). Deve abrir confirmação antes de executar. */
  danger?: boolean
  /** Separador antes do item. */
  divider?: boolean
  hidden?: boolean
  /**
   * Item visível mas indisponível: não executa, leva `aria-disabled` e o motivo no tooltip e no
   * nome acessível (ação desabilitada sempre diz por quê — BRAND.md "Tabelas de dados").
   */
  disabledReason?: string
}

type RowActionsProps = {
  /** Nome do registro, usado nos nomes acessíveis ("Alterar Hospital X"). */
  itemName: string
  /**
   * Ação principal visível (ex.: "Alterar"). Com `icon`, vira botão só de ícone (o `label`
   * vira tooltip e nome acessível). No mobile ela vai para dentro do menu.
   */
  primaryAction?: { label: string; onClick: () => void; icon?: IconPaths }
  items?: RowActionItem[]
  /**
   * Chamado quando o menu "⋯" abre. Para itens que dependem de dado que a linha não traz (ex.:
   * buscar o registro completo e só então mostrar uma ação): os `items` podem mudar com o menu aberto.
   */
  onOpen?: () => void
  /**
   * Enquanto `onOpen` busca o que falta, o menu mostra só "Carregando…": os itens aparecem todos de
   * uma vez, sem a lista pular sob o cursor quando um item novo entra no meio.
   */
  loading?: boolean
}

/**
 * Coluna de ações: ação principal (texto ou ícone) + menu "⋯".
 * O menu é o Dropdown do react-bootstrap (setas, Esc e foco já tratados).
 */
export const RowActions = ({
  itemName,
  primaryAction,
  items = [],
  onOpen,
  loading = false,
}: RowActionsProps) => {
  const visibleItems = items.filter((item) => !item.hidden)
  const menuLabel = `Mais ações para ${itemName}`
  const hasMenu = visibleItems.length > 0
  // Abaixo de `md` a ação principal sai da linha e entra no menu.
  const telaEstreita = useMediaQuery({ maxWidth: 767.98 })

  return (
    <div className={styles.actions}>
      {primaryAction?.icon && (
        <button
          type='button'
          className={clsx(styles.iconButton, hasMenu && 'd-none d-md-grid')}
          onClick={primaryAction.onClick}
          aria-label={`${primaryAction.label} ${itemName}`}
          title={primaryAction.label}
        >
          <span aria-hidden='true' className='d-flex'>
            <KTSVG path={primaryAction.icon} size={IconSize.ExtraSmall} />
          </span>
        </button>
      )}
      {primaryAction && !primaryAction.icon && (
        <Button
          variant='secondary-outline'
          size='btn-sm'
          className={hasMenu ? 'd-none d-md-flex' : undefined}
          onClick={primaryAction.onClick}
          ariaLabel={`${primaryAction.label} ${itemName}`}
        >
          {primaryAction.label}
        </Button>
      )}
      {hasMenu && (
        // Seta para baixo no gatilho abre o menu já com o foco no primeiro item.
        <BsDropdown
          align='end'
          focusFirstItemOnShow='keyboard'
          onToggle={(aberto) => aberto && onOpen?.()}
        >
          <BsDropdown.Toggle
            as='button'
            type='button'
            bsPrefix={styles.iconButton}
            aria-label={menuLabel}
          >
            <span aria-hidden='true' className='d-flex'>
              <KTSVG path={IconPaths.DotsHorizontal} size={IconSize.ExtraSmall} />
            </span>
          </BsDropdown.Toggle>
          <BsDropdown.Menu
            className={styles.menu}
            aria-label={menuLabel}
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
          >
            {/* Só no estreito: escondido por CSS ele continuaria sendo o 1º item do teclado e a
                seta para baixo tentaria focar um elemento invisível. */}
            {!loading && primaryAction && telaEstreita && (
              <BsDropdown.Item
                as='button'
                className={styles.menuItem}
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </BsDropdown.Item>
            )}
            {!loading &&
              visibleItems.map((item) => (
                <Fragment key={item.label}>
                  {item.divider && <BsDropdown.Divider />}
                  {item.disabledReason ? (
                    // Botão comum (e não Dropdown.Item, que sobrescreve `aria-disabled`): continua
                    // focável pelas setas do menu, para o motivo ser lido no tooltip.
                    <button
                      type='button'
                      className={clsx('dropdown-item', styles.menuItem, 'opacity-75')}
                      aria-disabled='true'
                      aria-label={`${item.label}: ${item.disabledReason}`}
                      title={item.disabledReason}
                      onClick={(e: MouseEvent) => e.preventDefault()}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <BsDropdown.Item
                      as='button'
                      className={clsx(styles.menuItem, item.danger && styles.menuItemDanger)}
                      onClick={item.onClick}
                    >
                      {item.label}
                    </BsDropdown.Item>
                  )}
                </Fragment>
              ))}
            {loading && (
              // Não é item de menu (não recebe foco nas setas): só avisa que as ações estão a caminho.
              <div role='status' className={clsx(styles.menuItem, styles.menuLoading)}>
                <span aria-hidden='true' className='spinner-border spinner-border-sm' />
                Carregando…
              </div>
            )}
          </BsDropdown.Menu>
        </BsDropdown>
      )}
    </div>
  )
}
